import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const SystemMonitoring = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');

  const timeframeOptions = [
    { value: '1h', label: 'Last Hour' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' }
  ];

  const systemMetrics = {
    apiRequests: {
      total: 45672,
      successful: 44891,
      failed: 781,
      avgResponseTime: 245
    },
    activeJobs: {
      running: 12,
      queued: 34,
      completed: 1247,
      failed: 23
    },
    systemHealth: {
      cpu: 67,
      memory: 78,
      disk: 45,
      network: 23
    },
    userActivity: {
      activeUsers: 456,
      newRegistrations: 23,
      subscriptions: 8,
      cancellations: 2
    }
  };

  const recentJobs = [
    {
      id: 1,
      type: "Bulk Valuation",
      user: "john.mitchell@cranedealer.com",
      status: "Running",
      progress: 67,
      startTime: "2025-11-06 15:45",
      estimatedCompletion: "2025-11-06 16:15",
      itemsProcessed: 134,
      totalItems: 200
    },
    {
      id: 2,
      type: "CSV Import",
      user: "s.chen@financecorp.com",
      status: "Completed",
      progress: 100,
      startTime: "2025-11-06 14:30",
      completionTime: "2025-11-06 14:45",
      itemsProcessed: 89,
      totalItems: 89
    },
    {
      id: 3,
      type: "Report Generation",
      user: "admin@craneprice.com",
      status: "Failed",
      progress: 45,
      startTime: "2025-11-06 13:20",
      failureTime: "2025-11-06 13:35",
      itemsProcessed: 23,
      totalItems: 50,
      error: "Database connection timeout"
    },
    {
      id: 4,
      type: "Portfolio Sync",
      user: "emily.johnson@constructionco.com",
      status: "Queued",
      progress: 0,
      queuePosition: 3,
      estimatedStart: "2025-11-06 16:30",
      totalItems: 156
    }
  ];

  const systemAlerts = [
    {
      id: 1,
      type: "Warning",
      message: "High memory usage detected on server-02",
      timestamp: "2025-11-06 15:30",
      severity: "Medium",
      resolved: false
    },
    {
      id: 2,
      type: "Error",
      message: "Database connection pool exhausted",
      timestamp: "2025-11-06 14:15",
      severity: "High",
      resolved: true
    },
    {
      id: 3,
      type: "Info",
      message: "Scheduled maintenance completed successfully",
      timestamp: "2025-11-06 12:00",
      severity: "Low",
      resolved: true
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Running': return 'bg-accent/10 text-accent';
      case 'Completed': return 'bg-success/10 text-success';
      case 'Failed': return 'bg-error/10 text-error';
      case 'Queued': return 'bg-warning/10 text-warning';
      default: return 'bg-muted text-text-secondary';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return 'bg-error/10 text-error';
      case 'Medium': return 'bg-warning/10 text-warning';
      case 'Low': return 'bg-success/10 text-success';
      default: return 'bg-muted text-text-secondary';
    }
  };

  const getHealthColor = (percentage) => {
    if (percentage >= 80) return 'text-error';
    if (percentage >= 60) return 'text-warning';
    return 'text-success';
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-text-primary">System Monitoring</h2>
        <div className="flex items-center space-x-3">
          <Select
            options={timeframeOptions}
            value={selectedTimeframe}
            onChange={setSelectedTimeframe}
          />
          <Button
            variant="outline"
            onClick={() => console.log('Refresh metrics')}
            iconName="RefreshCw"
            iconPosition="left"
            size="sm"
          >
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => console.log('Export metrics')}
            iconName="Download"
            iconPosition="left"
            size="sm"
          >
            Export
          </Button>
        </div>
      </div>
      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-text-primary">CPU Usage</h3>
            <Icon name="Cpu" size={16} className="text-text-secondary" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-2xl font-semibold ${getHealthColor(systemMetrics?.systemHealth?.cpu)}`}>
                {systemMetrics?.systemHealth?.cpu}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  systemMetrics?.systemHealth?.cpu >= 80 ? 'bg-error' :
                  systemMetrics?.systemHealth?.cpu >= 60 ? 'bg-warning' : 'bg-success'
                }`}
                style={{ width: `${systemMetrics?.systemHealth?.cpu}%` }}
              />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-text-primary">Memory Usage</h3>
            <Icon name="HardDrive" size={16} className="text-text-secondary" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-2xl font-semibold ${getHealthColor(systemMetrics?.systemHealth?.memory)}`}>
                {systemMetrics?.systemHealth?.memory}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  systemMetrics?.systemHealth?.memory >= 80 ? 'bg-error' :
                  systemMetrics?.systemHealth?.memory >= 60 ? 'bg-warning' : 'bg-success'
                }`}
                style={{ width: `${systemMetrics?.systemHealth?.memory}%` }}
              />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-text-primary">Disk Usage</h3>
            <Icon name="Database" size={16} className="text-text-secondary" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-2xl font-semibold ${getHealthColor(systemMetrics?.systemHealth?.disk)}`}>
                {systemMetrics?.systemHealth?.disk}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  systemMetrics?.systemHealth?.disk >= 80 ? 'bg-error' :
                  systemMetrics?.systemHealth?.disk >= 60 ? 'bg-warning' : 'bg-success'
                }`}
                style={{ width: `${systemMetrics?.systemHealth?.disk}%` }}
              />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-text-primary">Network I/O</h3>
            <Icon name="Wifi" size={16} className="text-text-secondary" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-2xl font-semibold ${getHealthColor(systemMetrics?.systemHealth?.network)}`}>
                {systemMetrics?.systemHealth?.network}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  systemMetrics?.systemHealth?.network >= 80 ? 'bg-error' :
                  systemMetrics?.systemHealth?.network >= 60 ? 'bg-warning' : 'bg-success'
                }`}
                style={{ width: `${systemMetrics?.systemHealth?.network}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* API and Job Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Statistics */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">API Statistics</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Total Requests</span>
              <span className="font-semibold text-text-primary">{systemMetrics?.apiRequests?.total?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Successful</span>
              <span className="font-semibold text-success">{systemMetrics?.apiRequests?.successful?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Failed</span>
              <span className="font-semibold text-error">{systemMetrics?.apiRequests?.failed?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Avg Response Time</span>
              <span className="font-semibold text-text-primary">{systemMetrics?.apiRequests?.avgResponseTime}ms</span>
            </div>
          </div>
        </div>

        {/* Job Statistics */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Job Processing</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Running Jobs</span>
              <span className="font-semibold text-accent">{systemMetrics?.activeJobs?.running}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Queued Jobs</span>
              <span className="font-semibold text-warning">{systemMetrics?.activeJobs?.queued}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Completed Today</span>
              <span className="font-semibold text-success">{systemMetrics?.activeJobs?.completed}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">Failed Today</span>
              <span className="font-semibold text-error">{systemMetrics?.activeJobs?.failed}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Jobs */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-text-primary">Recent Jobs</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Job Type</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">User</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Status</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Progress</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Time</th>
                <th className="text-right p-4 text-sm font-medium text-text-primary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentJobs?.map((job) => (
                <tr key={job?.id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-4">
                    <span className="font-medium text-text-primary">{job?.type}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-text-secondary">{job?.user}</span>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job?.status)}`}>
                      {job?.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>{job?.progress}%</span>
                        <span className="text-text-secondary">
                          {job?.itemsProcessed}/{job?.totalItems}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="h-2 bg-primary rounded-full transition-all duration-300"
                          style={{ width: `${job?.progress}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm">
                      <p className="text-text-primary">Started: {job?.startTime}</p>
                      {job?.status === 'Running' && job?.estimatedCompletion && (
                        <p className="text-text-secondary">ETA: {job?.estimatedCompletion}</p>
                      )}
                      {job?.status === 'Completed' && job?.completionTime && (
                        <p className="text-success">Completed: {job?.completionTime}</p>
                      )}
                      {job?.status === 'Failed' && job?.failureTime && (
                        <p className="text-error">Failed: {job?.failureTime}</p>
                      )}
                      {job?.status === 'Queued' && job?.estimatedStart && (
                        <p className="text-text-secondary">Est. Start: {job?.estimatedStart}</p>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => console.log('View job details:', job?.id)}
                        iconName="Eye"
                      />
                      {job?.status === 'Running' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => console.log('Cancel job:', job?.id)}
                          iconName="Square"
                          className="text-error hover:text-error"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* System Alerts */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-primary">System Alerts</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => console.log('Clear resolved alerts')}
              iconName="Trash2"
              iconPosition="left"
            >
              Clear Resolved
            </Button>
          </div>
        </div>
        <div className="divide-y divide-border">
          {systemAlerts?.map((alert) => (
            <div key={alert?.id} className={`p-4 ${alert?.resolved ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <Icon 
                    name={alert?.type === 'Error' ? 'AlertCircle' : alert?.type === 'Warning' ? 'AlertTriangle' : 'Info'} 
                    size={20} 
                    className={
                      alert?.type === 'Error' ? 'text-error' :
                      alert?.type === 'Warning' ? 'text-warning' : 'text-accent'
                    }
                  />
                  <div className="space-y-1">
                    <p className="font-medium text-text-primary">{alert?.message}</p>
                    <div className="flex items-center space-x-3 text-sm text-text-secondary">
                      <span>{alert?.timestamp}</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(alert?.severity)}`}>
                        {alert?.severity}
                      </span>
                      {alert?.resolved && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                          Resolved
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {!alert?.resolved && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => console.log('Resolve alert:', alert?.id)}
                    iconName="Check"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SystemMonitoring;